import * as Sentry from '@sentry/vue'
import Utils from '@/util'
import Constants from '@/constant'
import Socket from '@/socket'
import SlideApi from '@/api/slide'
import PresentationApi from '@/api/presentation'
import EventBus from '@/eventBus'
import store from '@/vuex/store'
import { waitForFrameBinding } from '@/services/office/frame-binding'

const isPresentingParamSet = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('presenting') === 'true'
  } catch (_error) {
    return false
  }
}

// PRO6-10: the export-pdf-via-cf worker loads the /share/:id view once per slide
// and needs to land directly on a given slide instead of always the first one.
// Reads `?slideNumber=N` (1-indexed, human-facing) straight off the URL, mirroring
// isPresentingParamSet() above — this file has no $route access.
const getSlideNumberQueryParam = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('slideNumber')
  } catch (_error) {
    return null
  }
}

/**
 * in case user visit presentation with ?presenting=true directly
 * there is a lag when presentation?.presenting is false
 * so we need to check url param to fix this issue
 */
const isPresentationPresenting = (presentation) => {
  return presentation?.presenting || isPresentingParamSet()
}

export default {

  /**
     * This method affect logic of the right tab when reload the presentation
     * It seems that for most type of slide, the activeTab is `Content`
     * or for the rest the activeTab is `Type`
     * @param {*} isNewPresentation
     * @param {*} sharePresentation
     * 2 above parameters are used for edge cases I don't understand
     * @param {*} _dispatch: $store.dispatch function to apply the change
     * @param {*} res: the response for presentation data from server
     */

  handleActiveTab(editor, _dispatch, res) {
    const { isNewPresentation, sharePresentation } = editor
    const hasSlidesWithNoType = res.body && res.body.slides && res.body.slides.length &&
      isNewPresentation && !res.body.slides[0].type
    const hasSlidesWithNoActiveSlide = res.body && res.body.slides &&
      !res.body.activeSlide && res.body.slides.length
    const hasNoRightNavActiveTab = !editor.rightNavActiveTab || !editor.rightNavActiveTab.length

    if ((hasSlidesWithNoType || hasSlidesWithNoActiveSlide) && hasNoRightNavActiveTab) {
      editor.showSlideTypeSelectionAction()
    }
    if (res.body.activeSlide) {
      return this._setActiveTabWhenHasActiveSlide(editor, sharePresentation, _dispatch, res)
    }
    _dispatch('setSlideActive', res.body.slides[0])
    return undefined
  },

  _setActiveTabWhenHasActiveSlide(editor, sharePresentation, _dispatch, res) {
    // On a reload the fetch can beat the frame's binding; falling back to res.body.activeSlide then
    // shows, and publishes, the server's slide instead of the bound one.
    if (store.state.isPowerPointAddIn) {
      return waitForFrameBinding().then(() => this._activateBoundOrServerSlide(sharePresentation, _dispatch, res))
    }
    this._activateBoundOrServerSlide(sharePresentation, _dispatch, res)
    return undefined
  },

  _activateBoundOrServerSlide(sharePresentation, _dispatch, res) {
    let activeSlide = null
    if (store.state.powerPointPresentationId === res.body.id && store.state.powerPointSlideId) {
      activeSlide = store.state.powerPointSlideId
    } else {
      activeSlide = res.body.activeSlide
    }

    const index = res.body.slides.findIndex((item) => {
      return item.id == activeSlide
    })

    if (index != -1) {
      if (sharePresentation) {
        // FIXME: cần sửa loại đoạn code này cho đẹp hơn, đoạn code này đang
        // là cách fix tạm thời từ tech debt https://ahaslides.atlassian.net/browse/AHA-6095
        // Vì hiện tại đang bị duplicate gọi API trên màn hình sharing nên
        // khi load xong data cần gọi đến hàm set slide active
        // thay vì update để đảm bảo các component đc render đúng.
        _dispatch('updateSlideActive', {
          id: null,
        })
      }
      _dispatch('setSlideActive', res.body.slides[index])
    }
  },

  setPrezFontFamily: (fontFamily) => {
    if (!fontFamily) return
    Utils.setFontSlideV2(fontFamily)
  },

  getAudienceOnline: (res, slideActive, presentation) => {
    const isPluginWithJoin = Boolean(slideActive?.typeObj?.plugin && slideActive?.typeObj?.setting?.enableAudienceJoinPresentation)
    const isPresentingQuizOrSpinnerWheel = (store.getters['slideModule/isQuizSlide'](slideActive) &&
      isPresentationPresenting(presentation)) || slideActive.type === 'spinnerWheel' || isPluginWithJoin
    if (!isPresentingQuizOrSpinnerWheel) return
    try {
      // This runs on EVERY presentation-detail load, including passive ones (a
      // second tab, a collaborator, an admin, a mid-quiz refresh). Fetch the
      // online count but do NOT reset the answered counter here, or we delete the
      // shared live count for everyone already presenting (AHA-46259). The genuine
      // reset happens on start-presenting (EditorHeader) and clear-results.
      PresentationApi.getAudienceOnline({
        accessCode: res.body.uniqueAccessCode,
      }, { skipReset: true })
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  setPresentationSockets: (currentUser) => {
    if (!currentUser) return {}
    const {
      id, avatar, firstName, lastName, email, accountRole,
    } = currentUser
    return {
      id,
      avatar,
      name: lastName ? `${firstName} ${lastName}` : firstName,
      socketId: Socket.getSocketId(),
      email,
      accountRole,
    }
  },

  setOnlineStatus: (currentUser, sharePresentation, userOnline, onlineStatus, plan) => {
    let result = 0
    if (!currentUser.id) return result
    if (!sharePresentation) {
      const percentLimit =
        (userOnline / plan.limit) * 100

      if (percentLimit > 75 && percentLimit < 95) {
        result = 2
      }
      if (percentLimit >= 95) {
        result = 3
      }
      if (percentLimit <= 75 && onlineStatus != 1) {
        result = 1
      }
    } else {
      result = 1
    }
    return result
  },

  setPresentationLanguage: (res, _dispatch) => {
    if (!res.body.language) {
      res.body.language = 'en'
    }
    _dispatch('setLang', res.body.language)
  },

  setBrowserTitle: (res, isPresenterWhiteLabel, isBackstage) => {
    if (!isPresenterWhiteLabel) {
      window.document.title = `${res.body.name} - AhaSlides`
    }

    if (isBackstage) {
      window.document.title = `Backstage of ${res.body.name}`
      store.dispatch('preventLockScreen/requestWakeLockAction')
    }
  },

  _getSlideType(slide) {
    if (!slide.slideType) slide.slideType = null
    let obj = {}
    const allTypeSections = store.getters['slideTypeList/getSlideTypeList']
    allTypeSections.forEach((item) => {
      item.slideTypes.forEach((typeObj) => {
        if (!typeObj.slideType) typeObj.slideType = null
        if (
          typeObj.type == slide.type &&
          typeObj.slideType == slide.slideType
        ) {
          obj = typeObj
        }
      })
    })
    if (!obj.type) {
      const marketplaceSlideTypes = store.getters['slideMarketplace/marketplaceSlideTypes']
      if (marketplaceSlideTypes) {
        const marketplaceSlide = marketplaceSlideTypes.find((s) => s.type === slide.type)
        if (marketplaceSlide) {
          obj = {
            ...marketplaceSlide,
            plugin: true,
          }
        }
      }
    }
    return obj
  },
  sortAndUpdateSlidesV2(res, editor) {
    if (!res.body.slides || res.body.slides?.length === 0) return
    if (editor.isNewPresentation) {
      res.body.slides[0].type = ''
    }
    this._setSlidesOtherSettings(res)

    res.body.slides.sort((a, b) => {
      return a.order - b.order
    })
    this._updateNextSlide(res, editor, editor.slideActive,
      editor.sharePresentation, editor.$route.params.id)
    this._setSlideSettingsWhenSharePresentation(res, editor.sharePresentation)
  },

  sortAndUpdateSlides(res, editor) {
    if (!res.body.slides) return
    if (editor.isNewPresentation) {
      res.body.slides[0].type = ''
    }
    this._setSlidesOtherSettings(res)

    res.body.slides.sort((a, b) => {
      return a.order - b.order
    })
    this._updateNextSlide(res, editor, editor.slideActive,
      editor.sharePresentation, editor.$route.params.id)
    this._setSlideSettingsWhenSharePresentation(res, editor.sharePresentation)
  },

  _setSlidesOtherSettings(res) {
    res.body.slides.forEach((item) => {
      if (!item.multipleCountAnswer) item.multipleCountAnswer = 0
      item.voteCache = item.multipleCountAnswer
      if (item.type) {
        item.typeObj = this._getSlideType(item)
      }

      try {
        if (item.additionalFields) {
          item.additionalFields = JSON.parse(item.additionalFields)
        } else {
          item.additionalFields = []
        }
      } catch (error) {
        item.additionalFields = []
      }

      try {
        if (item.otherCorrectQuiz) {
          item.otherCorrectQuiz = JSON.parse(item.otherCorrectQuiz)
        } else {
          item.otherCorrectQuiz = []
        }
      } catch (error) {
        item.otherCorrectQuiz = []
        Sentry.captureException(error)
      }

      item.SlideOptions.sort((a, b) => {
        if (item.type == 'openEnded') {
          return Utils.sortOptionsByDate(a, b)
        }
        return a.order - b.order
      })
      if (item.hints && item.hints.length) {
        item.hints.sort((a, b) => {
          return a.order - b.order
        })
      }
    })
  },

  _updateNextSlide: async (res, editor, slideActive, sharePresentation, routerId) => {
    const editorHasSlidesWithNoActiveSlide =
      !res.body.activeSlide && res.body.slides.length && !sharePresentation
    if (!editorHasSlidesWithNoActiveSlide) return

    try {
      await SlideApi.updateNextSlideById(routerId || res.body.id, {
        activeSlide: res.body.slides[0].id,
        accessCode: res.body.uniqueAccessCode,
        from: 'PresentationDetailUtils',
        presentationId: res.body.id,
      })
    } catch (error) {
      if (error.status) {
        Sentry.captureException(`Presenter put next slide main, status: ${error.status}`)
      }
    }

    editor.$store.dispatch('checkSlideMovingDirectionAction', slideActive.id)
  },

  _setSlideSettingsWhenSharePresentation(res, sharePresentation) {
    if (!sharePresentation) return
    res.body.slides.forEach((item) => {
      item.hideResult = false
      item.showAllBulletPoints = true
      item.showCorrectOption = true
      if (item.image && item.type !== 'ideas') {
        item.showQuestionImage = true
      }
      if (item.type === 'ideas') {
        item.showQuestionImage = false
        item.votingStep = Constants.IdeasTabName.RESULT
      }
    })
    const slideIdFromQueryParam = this._getShareSlideIdFromQueryParam(res.body.slides)
    res.body.activeSlide = slideIdFromQueryParam !== undefined
      ? slideIdFromQueryParam
      : res.body.slides[0].id
  },

  // PRO6-10: resolves the `?slideNumber=N` override (see getSlideNumberQueryParam
  // above) to a slide id, or undefined when the param is missing/invalid/out of
  // range — the caller then falls back to slides[0].id, the original default.
  // This computes the initial active slide for a share view in the SAME dispatch
  // that used to unconditionally force slide 0, so there's only one place that
  // ever decides it. Previously PresentationSharingView.selectSlideFromQueryParam()
  // ran as a second, later dispatch that overrode this one — a reviewer flagged
  // that as a race condition on PR #4852: anything that re-triggered the same
  // load/active-slide logic between the two dispatches (e.g. the
  // EventBus 'getDataPresentationDetail' listener, a socket resync) would
  // silently reset activeSlide to slide 0 and stomp the override with no error.
  // Collapsing to one dispatch removes that window entirely.
  _getShareSlideIdFromQueryParam(slides) {
    const slideNumber = getSlideNumberQueryParam()
    if (slideNumber === undefined || slideNumber === null || slideNumber === '') return undefined

    const parsedSlideNumber = parseInt(slideNumber, 10)
    if (!Number.isInteger(parsedSlideNumber)) return undefined

    const index = parsedSlideNumber - 1
    if (index < 0 || index >= slides.length) return undefined

    const targetSlide = slides[index]
    return targetSlide ? targetSlide.id : undefined
  },

  detectScreenAndSetTransition: () => {
    setTimeout(() => {
      EventBus.$emit('detectScreen')
      EventBus.$emit('eventHandleSetValueTransition')
    }, 500)
  },

  /**
   * Orchestration function to load and sync slide annotations after presentation data is loaded
   * This is coordination/orchestration code that dispatches Vuex actions
   * @param {Object} editorContext - The Vue component context (this)
   */
  loadAndSyncSlideAnnotations: async (editorContext) => {
    try {
      // Load and sync slide annotations (non-blocking)
      // This runs after presentation data is committed to state
      await editorContext.fetchAnnotationsAction()
      await editorContext.syncAnnotationsAction()
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}
